import "./polyfills";
import React, { Fragment, useState, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import "./index.scss";
import * as serviceWorker from "./serviceWorker";
import { Provider } from "react-redux";
import store from "./store";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import ConfigDB from "./data/customizer/config";
import { routes } from "config/routes";
import Login from "components/Login/Login";
import { AuthContext } from "app/app-context";
import { AppUser } from "models/AppUser";
import WebFont from "webfontloader";
import { appTranslation } from "app/app-translation";

WebFont.load({
  google: {
    // Font của app, khai cùng tên với --app-font trong src/assets/scss/theme/_tokens.scss.
    families: ["Inter:400,500,600,700:vietnamese,latin"],
  },
});

const App = React.lazy(async () => {
  await appTranslation.initialize();
  return import("app/app");
});

const container = document.getElementById("root");
const root = createRoot(container!);

const Root = () => {
  const [anim, setAnim] = useState("");
  const [appUserInfo, setAppUserInfo] = useState<AppUser>(null);
  const animation =
    localStorage.getItem("animation") ||
    ConfigDB.data.router_animation ||
    "fade";
  const abortController = new AbortController();

  const routeNodeRefs = useMemo(() => {
    const refs = new Map<string, React.RefObject<HTMLDivElement>>();
    routes.forEach(({ path }) => {
      refs.set(path, React.createRef<HTMLDivElement>());
    });
    return refs;
  }, []);

  useEffect(() => {
    setAnim(animation);
    return function cleanup() {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <Provider store={store}>
        <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
          <React.Suspense fallback={null}>
            <Switch>
              <Route
                exact
                path="/login"
                render={() => {
                  return <Login />;
                }}
              />
              <Route
                render={() => {
                  return (
                    <AuthContext.Provider value={[appUserInfo, setAppUserInfo]}>
                      <App>
                        <TransitionGroup>
                          <Switch>
                            <Route
                              exact
                              path="/"
                              render={() => {
                                return (
                                  <Redirect
                                    to="/dashboard/default"
                                  />
                                );
                              }}
                            />
                            {routes.map(({ path, component: Component }) => {
                              const nodeRef =
                                routeNodeRefs.get(path) ??
                                React.createRef<HTMLDivElement>();
                              return (
                                <Route
                                  key={path}
                                  path={path}
                                  render={(props) => {
                                    return (
                                      <CSSTransition
                                        in={props.match != null}
                                        timeout={100}
                                        classNames={anim}
                                        unmountOnExit
                                        nodeRef={nodeRef}
                                      >
                                        <div ref={nodeRef}>
                                          <Component />
                                        </div>
                                      </CSSTransition>
                                    );
                                  }}
                                ></Route>
                              );
                            })}
                            <Redirect
                              exact
                              from="/"
                              to="/dashboard/default"
                            />
                          </Switch>
                        </TransitionGroup>
                      </App>
                    </AuthContext.Provider>
                  );
                }}
              />
            </Switch>
          </React.Suspense>
        </BrowserRouter>
      </Provider>
    </Fragment>
  );
};

root.render(
  // <React.StrictMode>
    <Root />
  // </React.StrictMode>
);

serviceWorker.unregister();
